import { useIsMobile } from "@/hooks/use-mobile";
import { useCalendarDefaults } from "@/hooks/useCalendarDefaults";
import { calculateOptimalTimeRange, normalizeTimeFormat } from "@/utils/timeUtils";

interface CalendarConfigProps {
  service?: any;
}

export const useCalendarConfig = ({ service }: CalendarConfigProps) => {
  const isMobile = useIsMobile();
  const { calendarDefaults } = useCalendarDefaults();
  
  // Get business hours configuration
  const getBusinessHoursConfig = () => {
    // First, check if service has specific business hours
    if (service) {
      const days = [];
      const businessHours = [];
      
      // Check each day of the week
      if (service.monday_start && service.monday_end) {
        businessHours.push({
          daysOfWeek: [1], // Monday
          startTime: service.monday_start,
          endTime: service.monday_end
        });
      }
      
      if (service.tuesday_start && service.tuesday_end) {
        businessHours.push({
          daysOfWeek: [2], // Tuesday
          startTime: service.tuesday_start,
          endTime: service.tuesday_end
        });
      }
      
      if (service.wednesday_start && service.wednesday_end) {
        businessHours.push({
          daysOfWeek: [3], // Wednesday
          startTime: service.wednesday_start,
          endTime: service.wednesday_end
        });
      }
      
      if (service.thursday_start && service.thursday_end) {
        businessHours.push({
          daysOfWeek: [4], // Thursday
          startTime: service.thursday_start,
          endTime: service.thursday_end
        });
      }
      
      if (service.friday_start && service.friday_end) {
        businessHours.push({
          daysOfWeek: [5], // Friday
          startTime: service.friday_start,
          endTime: service.friday_end
        });
      }
      
      if (service.saturday_start && service.saturday_end) {
        businessHours.push({
          daysOfWeek: [6], // Saturday
          startTime: service.saturday_start,
          endTime: service.saturday_end
        });
      }
      
      if (service.sunday_start && service.sunday_end) {
        businessHours.push({
          daysOfWeek: [0], // Sunday
          startTime: service.sunday_start,
          endTime: service.sunday_end
        });
      }
      
      // If service has specific business hours, return them
      if (businessHours.length > 0) {
        return businessHours;
      }
    }
    
    // If no service-specific hours, use admin defaults
    if (calendarDefaults?.business_hours && calendarDefaults.business_hours.length > 0) {
      return calendarDefaults.business_hours;
    }
    
    // Fallback to hardcoded defaults if admin settings are not available
    return [
      {
        daysOfWeek: [1, 2, 3, 4, 5], // Monday - Friday
        startTime: '09:00',
        endTime: '17:00'
      }
    ];
  };
  
  // Get toolbar configuration
  const getToolbarConfig = () => {
    return {
      start: 'prev,next today',
      center: 'title',
      end: 'dayGridMonth,timeGridWeek,timeGridDay'
    };
  };
  
  // Get time range configuration with dynamic adjustment
  const getTimeRange = () => {
    const adminTimeRange = calendarDefaults?.time_range || {
      minTime: '08:00:00',
      maxTime: '20:00:00'
    };

    // Use service-specific display times if available
    const serviceDisplayRange = {
      minTime: service?.display_start_time ? normalizeTimeFormat(service.display_start_time) : adminTimeRange.minTime,
      maxTime: service?.display_end_time ? normalizeTimeFormat(service.display_end_time) : adminTimeRange.maxTime
    };

    // Get the optimal time range based on operating hours
    const businessHours = getBusinessHoursConfig();
    const optimalRange = calculateOptimalTimeRange(businessHours, serviceDisplayRange);

    return optimalRange;
  };
  
  // Get calendar views configuration
  const getCalendarViews = () => {
    // Use service-specific slot_size if available, otherwise fall back to admin defaults
    let slotDuration = calendarDefaults?.slot_duration || '00:30:00';
    
    if (service?.slot_size) {
      switch (service.slot_size) {
        case '15':
          slotDuration = '00:15:00';
          break;
        case '30':
          slotDuration = '00:30:00';
          break;
        case '60':
          slotDuration = '01:00:00';
          break;
        case '90':
          slotDuration = '01:30:00';
          break;
        case '120':
          slotDuration = '02:00:00';
          break;
        case 'allDay':
          slotDuration = '24:00:00';
          break;
        default:
          // If it's a number, convert to time format
          if (!isNaN(Number(service.slot_size))) {
            const minutes = Number(service.slot_size);
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            slotDuration = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
          }
      }
    }
    
    return {
      dayGrid: {
        dayMaxEventRows: 3
      },
      timeGrid: {
        dayMaxEventRows: 6,
        slotDuration: slotDuration,
        slotLabelInterval: '01:00'
      }
    };
  };
  
  // Get calendar height configuration
  const getCalendarHeight = () => {
    return 'auto';
  };

  // Get initial view configuration
  const getInitialView = () => {
    // Use the mobile-specific view if on mobile
    if (isMobile) {
      return "timeGridDay"; // Always use day view on mobile
    }
    
    // Otherwise use week view for desktop
    return "timeGridWeek"; // Changed from timeGridDay to timeGridWeek for desktop
  };
  
  return {
    getBusinessHoursConfig,
    getToolbarConfig,
    getTimeRange,
    getCalendarViews,
    getCalendarHeight,
    getInitialView
  };
};
